import { useQuery } from '@tanstack/react-query'

import { supabase } from '@/lib/supabase/cliente'
import { identificadorUnico } from '@/shared/utilidades/identificador'

/**
 * Las fotos de evidencia viven en un bucket PRIVADO ('evidencias'): no tienen
 * URL pública. Se suben con la sesión del operario (la política del bucket lo
 * deja subir solo a la carpeta de SUS órdenes) y se ven con una URL firmada
 * temporal.
 */
const BUCKET = 'evidencias'

/** Sube una foto al bucket y devuelve su ruta (<orden>/<uuid>.<ext>). */
export async function subirEvidencia(ordenTrabajoId: string, archivo: File): Promise<string> {
  const ext =
    archivo.type === 'image/png' ? 'png' : archivo.type === 'image/webp' ? 'webp' : 'jpg'
  const ruta = `${ordenTrabajoId}/${identificadorUnico()}.${ext}`

  const { error } = await supabase.storage
    .from(BUCKET)
    .upload(ruta, archivo, { contentType: archivo.type || 'image/jpeg', upsert: false })

  if (error) throw new Error(`No se pudo subir la foto: ${error.message}`)

  return ruta
}

/** URLs firmadas para ver un conjunto de fotos privadas. Se recalculan solas. */
export function useUrlesDeEvidencia(rutas: string[]) {
  const consulta = useQuery({
    queryKey: ['evidencia-urls', ...rutas],
    enabled: rutas.length > 0,
    // La firma dura una hora; se refresca antes de vencer.
    staleTime: 50 * 60 * 1000,
    queryFn: async (): Promise<string[]> => {
      const { data, error } = await supabase.storage.from(BUCKET).createSignedUrls(rutas, 3600)
      if (error) throw new Error(error.message)
      return (data ?? []).map((d) => d.signedUrl).filter((u): u is string => Boolean(u))
    },
  })

  return { urles: consulta.data ?? [], cargando: consulta.isLoading }
}
